import { Ajax } from "../../Common/Ajax";
import { CookieHelper } from "../../Common/CookieHelper";
import { GlobalData } from "../../Common/GlobalData";
import { AutoCompleteHelper, GridHelper, Notification, ReportDateHelper, TemplateHelpers } from "../../Common/KendoHelpers";
import { TabStrip } from "./TabStrip";

export class ReadyToBuyList {
    public static readonly GRID = '#readyToBuyListGrid';
    private static readonly TOTAL_NUM_STOCKS = '#totalNumStocks';
    private static readonly TOTAL_COST_BASIS = '#totalCostBasis';
    private static readonly TOTAL_PCT_INVESTED = '#totalPctInvested';
    private static readonly NEW_SYMBOL = '#newSymbol';
    private static readonly CREATE_BUTTON = '#createButton';

    private grid!: kendo.ui.Grid;
    private formatter!: Intl.NumberFormat;

    // This is a similar hack because the calculated values are cleared when the user clicks
    // the Cancel Changes button.
    private cancelChangesEventSet: boolean = false;
    // Each time the user enters a value in a cell, the grid's dataBound callback is called.
    // However, we don't need to do the same work in this case as we do when the grid first
    // loads. This flag indicates when to do that initial work.
    private calcValuesSet: boolean = false;

    private static _instance: ReadyToBuyList;
    public static getInstance() {
        return this._instance || (this._instance = new this());
    }

    private constructor() {}

    public init() {
        this.cancelChangesEventSet = false;
        this.calcValuesSet = false;

        this.formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            // These options are needed to round to whole numbers if that's what you want.
            //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
            //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
        });

        if (this.grid) {
            this.grid.destroy();
            $(ReadyToBuyList.GRID).empty();
        }
        this.initGrid();
    }

    private initGrid() {
        const dataSource: any = {
            transport: {
                read: GlobalData.URL_BASE +
                    GlobalData.URL_SUFFIX_PORTFOLIO_ACCOUNT_MANAGER_READY_TO_BUY_LIST +
                    '?account=' +
                    CookieHelper.get(GlobalData.PORTFOLIO_ACCOUNT_SELECTOR_COOKIE),
                update: {
                    url: GlobalData.URL_BASE +
                        GlobalData.URL_SUFFIX_PORTFOLIO_ACCOUNT_MANAGER_READY_TO_BUY_LIST +
                        '?account=' + CookieHelper.get(GlobalData.PORTFOLIO_ACCOUNT_SELECTOR_COOKIE),
                    type: 'PUT',
                    contentType: 'application/json',
                    dataType: 'json',
                },
                parameterMap: function (options: any, operation: any) {
                    if (operation !== "read" && options.models) {
                        return kendo.stringify(options.models);
                    }
                }
            },
            batch: true,
            serverPaging: false,
            serverSorting: false,
            schema: {
                model: {
                    id: "id",
                    fields: {
                        id: { editable: false },
                        symbol: { type: "string", nullable: false, editable: false },
                        companyName: { editable: false },
                        close: { editable: false },
                        closeChg: { editable: false },
                        sumQtyOwn: { editable: false },
                        risk: { editable: false },
                        totalRisk: { editable: true },
                        position: { editable: false },
                        calcShares: { editable: false },
                        shares: { editable: true },
                        calcBidPrice: { editable: false },
                        calcCost: { editable: false },
                        calcCurrPosition: { editable: false },
                        buyNow: { editable: true, type: "boolean" },
                    },
                }
            }
        };

        this.grid = $(ReadyToBuyList.GRID).kendoGrid({
            dataSource: dataSource,
            dataBound: () => {
                if (!this.calcValuesSet) {
                    this.calcValuesSet = true;

                    this.setCalcValues();
                    this.defineEvents();
                    this.updateTotals();
                }

                $(ReadyToBuyList.GRID).show();
            },

            saveChanges: () => {
                // This tells the dataBound event to recalculate all of the calculated
                // values and refresh the grid again. Otherwise the calculated values
                // get cleared out.
                this.calcValuesSet = false;
            },

            cancel: () => {
                // This tells the dataBound event to recalculate all of the calculated
                // values and refresh the grid again. Otherwise the calculated values
                // get cleared out.
                this.calcValuesSet = false;
            },

            height: 915,
            editable: true,
            groupable: false,
            navigatable: true,
            sortable: true,
            filterable: true,
            pageable: false,
            resizable: true,
            toolbar: [
                { name: "save" },
                { name: "cancel" },
            ],
            columns: [
                {
                    title: 'General Info',
                    width: '35%',
                    headerAttributes: { 'class': 'table-header-cell grid-header-14', 'style': 'font-size: 16px; font-weight: bold' },
                    columns: [
                        {
                            field: 'id',
                            hidden: true,
                        },
                        {
                            field: 'symbol',
                            title: 'Symbol',
                            width: '6%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                        },
                        {
                            field: 'companyName',
                            title: 'Company Name',
                            width: '15%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: left; font-size: 12px" },
                        },
                        {
                            field: "close",
                            title: "Close",
                            width: '6%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            format: '{0:c2}',
                        },
                        {
                            field: "closeChg",
                            title: "Close<br>Change",
                            width: '6%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            format: '{0:c2}',
                        },
                    ],
                },
                {
                    title: 'Current Positions',
                    width: '20%',
                    headerAttributes: { 'class': 'table-header-cell grid-header-14', 'style': 'font-size: 16px; font-weight: bold' },
                    columns: [
                        {
                            field: "sumQtyOwn",
                            title: "Num<br>Shares<br>Owned",
                            width: '6%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            format: '{0:n0}',
                        },
                        {
                            field: "risk",
                            title: "Current<br>Risk",
                            width: '6%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            format: '{0:p0}',
                        },
                    ],
                },
                {
                    title: 'New Positions',
                    width: '40%',
                    headerAttributes: { 'class': 'table-header-cell grid-header-14', 'style': 'font-size: 16px; font-weight: bold' },
                    columns: [
                        {
                            field: "totalRisk",
                            title: "Total<br>Risk",
                            editor: this.totalRiskEditor,
                            width: '6%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            format: '{0:p0}',
                        },
                        {
                            field: "position",
                            title: "New<br>Risk",
                            width: '6%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            format: '{0:p0}',
                        },
                        {
                            field: "calcShares",
                            title: "Action",
                            width: '5%',
                            filterable: false,
                            sortable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            template: kendo.template(
                                '# if (calcShares > 0) { #' +
                                '<span class="grid-action-buy">Buy</span>' +
                                '# } else if (calcShares < 0) { #' +
                                '<span class="grid-action-sell">Sell</span>' +
                                '# } #'
                            ),
                        },                        {
                            field: "calcShares",
                            title: "Calc<br>Shares",
                            width: '6%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            template: kendo.template(TemplateHelpers.getColorNumberTemplate('calcShares', 'n0')),
                        },                        {
                            field: "shares",
                            title: "New<br>Shares",
                            editor: this.sharesEditor,
                            width: '6%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            template: kendo.template(TemplateHelpers.getColorNumberTemplate('shares', 'n0')),
                        },
                        {
                            field: "calcBidPrice",
                            title: "Bid<br>Price",
                            width: '6%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            format: '{0:c2}',
                        },
                        {
                            field: "calcCost",
                            title: "Cost",
                            width: '6%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            template: kendo.template(TemplateHelpers.getColorCurrencyTemplate('calcCost')),
                        },
                    ],
                },
                {
                    title: 'Actions',
                    width: '8%',
                    headerAttributes: { 'class': 'table-header-cell grid-header-14', 'style': 'font-size: 16px; font-weight: bold' },
                    columns: [
                        {
                            field: 'buyNow',
                            title: 'Buy<br>Now',
                            width: '5%',
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            template: kendo.template(TemplateHelpers.getCheckboxTemplate('buyNow')),
                        },
                        {
                            title: 'Delete',
                            width: '3%',
                            filterable: false,
                            headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                            template: kendo.template(TemplateHelpers.getDeleteButtonTemplate('../images', 'symbol')),
                        },
                    ]
                },
            ]
        }).data('kendoGrid')!;
    }

    private defineEvents() {
        // Update value of the field when user clicks a checkbox.
        $('.buyNowCheckbox').off('click').on('click', (e: any) => {
            this.onBuyNowClick(e);
        });

        const deleteButtonClass = TemplateHelpers.getDeleteButtonClass();
        $('.' + deleteButtonClass).off('click').on('click', (e: any) => {
            this.onDeleteClick(e);
        });

        if (!this.cancelChangesEventSet) {
            this.cancelChangesEventSet = true;
            $('.k-grid-cancel-changes').off('click').on('click', () => {
                this.calcValuesSet = false;
            });
        }

        $(ReadyToBuyList.NEW_SYMBOL).off('keypress').on('keypress', (e: any) => {
            if ($(e)[0].key == 'Enter') {
                this.createItem();
            }
        });

        $(ReadyToBuyList.CREATE_BUTTON).off('click').on('click', () => {
            this.createItem();
        });
    }

    private updateTotals() {
        const metadata = TabStrip.getInstance().getMetadata();
        if (!metadata) {
            return;
        }

        let totalNumStocks = metadata.numStocks;
        let totalCostBasis = metadata.sumCostBasis;
        const data: any = this.grid.dataSource.data();

        if (data?.length == 0) {
            return;
        }

        for (const row of data) {
            if (!(row.risk > 0)) {
                ++totalNumStocks;
            }
            totalCostBasis += row.calcCost;
        }
        const totalPctInvested = Math.round(totalCostBasis / metadata.maxInvestment * 100);

        $(ReadyToBuyList.TOTAL_NUM_STOCKS).html(totalNumStocks.toString() + ' / ' + metadata.maxStocks);
        $(ReadyToBuyList.TOTAL_COST_BASIS).html(this.formatter.format(totalCostBasis) + ' / ' +
            this.formatter.format(metadata.maxInvestment));
        $(ReadyToBuyList.TOTAL_PCT_INVESTED).html(totalPctInvested + '%');
    }

    private setCalcValues() {
        const data: any = this.grid.dataSource.data();
        if (data && data.length > 0) {
            ReportDateHelper.showReportDate(data[0].closingDate);

            for (const row of data) {
                this.setCalcShares(row);
                this.setOtherCalcValues(row);
            }
            this.grid.refresh();
        }
    }

    private setCalcShares(row: any) {
        const maxInvestmentPerStock = row.maxInvestment / row.maxStocks;

        if (row.position && row.close) {
            row.calcShares = maxInvestmentPerStock * row.position / row.close;
        } else {
            row.position = null;
            row.calcShares = null;
        }
    }

    private setOtherCalcValues(row: any) {
        if (row.high && row.low && row.shares) {
            row.calcBidPrice = (row.high + row.low) / 2;
            row.calcCost = row.calcBidPrice * row.shares;
        } else {
            row.shares = null;
            row.calcBidPrice = null;
            row.calcCost = null;
        }
    }

    private totalRiskEditor(container: any, options: any) {
        const self = ReadyToBuyList.getInstance();

        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.off('blur').on('blur', (element: any) => {
            const gridData = self.grid.dataSource.data();
            const gridDataRow = GridHelper.getCurrentGridDataRow(gridData, element);
            // This is a hack to work around the issue that the actual value is between
            // 0 and 1, but the value we want to enter is between 1 and 100. This prevents
            // an update when the value doesn't change and it's still between 0 and 1.
            if (gridDataRow?.totalRisk > 1) {
                gridDataRow.totalRisk = (gridDataRow.totalRisk as number) / 100;
                gridDataRow.position = gridDataRow.totalRisk - gridDataRow.risk;
                self.setCalcShares(gridDataRow);
                self.updateTotals();
                self.grid.setDataSource(self.grid.dataSource);
            }
        });
    }

    private sharesEditor(container: any, options: any) {
        const self = ReadyToBuyList.getInstance();

        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.off('blur').on('blur', (element: any) => {
            const gridData = self.grid.dataSource.data();
            const gridDataRow = GridHelper.getCurrentGridDataRow(gridData, element);
            if (gridDataRow) {
                self.setOtherCalcValues(gridDataRow);
                self.updateTotals();
                self.grid.setDataSource(self.grid.dataSource);
            }
        });
    }

    private onBuyNowClick(e: any) {
        const dataItem = this.getClosestDataItem(e) as any;
        dataItem.buyNow = e.target.checked;

        this.updateBuyNow(dataItem);
    }

    private updateBuyNow(dataItem: any) {
        const success = () => {
            // Clear this flag so the dataBound callback will redefine the events and so on.
            this.calcValuesSet = false;
            this.grid.dataSource.read();
        };

        const error = (x: any) => {
            const msg = new Notification();
            msg.error(x.responseJSON);
        };

        Ajax.invoke(GlobalData.URL_SUFFIX_PORTFOLIO_ACCOUNT_MANAGER_READY_TO_BUY_LIST +
            '/BuyNow?account=' + CookieHelper.get(GlobalData.PORTFOLIO_ACCOUNT_SELECTOR_COOKIE) +
            '&symbol=' + dataItem.symbol + '&buyNow=' + dataItem.buyNow,
            'PUT', dataItem, success, error);
    }

    private onDeleteClick(e: any) {
        const deleteButtonClass = TemplateHelpers.getDeleteButtonClass();
        const symbol = e.currentTarget.id.substr(deleteButtonClass.length);

        kendo.confirm('Are you sure that you want to delete ' + symbol + '?')
            .then(() => {
                this.deleteRow(symbol);
            });
    }

    private deleteRow(symbol: string) {
        const success = () => {
            // Clear this flag so the dataBound callback will redefine the events and so on.
            this.calcValuesSet = false;
            this.grid.dataSource.read();
        };

        const error = (x: any) => {
            const msg = new Notification();
            msg.error(x.responseJSON);
        };

        Ajax.invoke(GlobalData.URL_SUFFIX_PORTFOLIO_ACCOUNT_MANAGER_READY_TO_BUY_LIST +
            '?account=' + CookieHelper.get(GlobalData.PORTFOLIO_ACCOUNT_SELECTOR_COOKIE) +
            '&symbol=' + symbol, 'DELETE', null, success, error);
    }

    private createItem() {
        const symbol = ($(ReadyToBuyList.NEW_SYMBOL).val() as string).toUpperCase();

        const success = () => {
            // Clear this flag so the dataBound callback will redefine the events and so on.
            this.calcValuesSet = false;
            this.grid.dataSource.read();
        };

        const error = (x: any) => {
            const msg = new Notification();
            msg.error(x.responseJSON);
        };

        Ajax.invoke(GlobalData.URL_SUFFIX_PORTFOLIO_ACCOUNT_MANAGER_READY_TO_BUY_LIST +
            '?account=' + CookieHelper.get(GlobalData.PORTFOLIO_ACCOUNT_SELECTOR_COOKIE) +
            '&symbol=' + symbol, 'POST', null, success, error);
    }

    private getClosestDataItem(e: any) {
        return this.grid.dataItem($(e.target).closest('tr'));
    }
}
